import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWeighing } from '@/contexts/WeighingContext';
import PermissionGuard from '@/components/permissions/PermissionGuard';
import { PERMISSIONS } from '@/types/permissions';
import { 
  ChartBarIcon, 
  PlusIcon, 
  CubeIcon, 
  BeakerIcon,
  // TruckIcon, // Non utilisé
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import statisticsService, { StatisticsData, TimeSeriesData } from '@/services/statisticsService';
import { format, startOfDay, endOfDay, subDays } from 'date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DashboardPage: React.FC = () => {
  const { clearWeighing } = useWeighing();
  const [chartPeriod, setChartPeriod] = useState('7');
  const [error, setError] = useState<string>('');
  const [stats, setStats] = useState({
    total_weighings_today: 0,
    total_rejections: 0,
    compliance_rate: 0,
    total_stock: 0,
    stock_capacity: 0
  });
  const [generalStats, setGeneralStats] = useState<StatisticsData | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  const [todayStats, setTodayStats] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);

  // Fonction pour gérer le clic sur "Nouvelle pesée"
  const handleNewWeighingClick = () => {
    // Réinitialiser le contexte de pesée
    clearWeighing();
  };

  // Charger les données du tableau de bord
  useEffect(() => {
    loadDashboardData();
  }, [chartPeriod]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      const today = new Date();
      const todayStart = format(startOfDay(today), 'yyyy-MM-dd');
      const todayEnd = format(endOfDay(today), 'yyyy-MM-dd');
      
      // Calculer la période pour les graphiques
      const periodDays = parseInt(chartPeriod);
      const chartStart = format(startOfDay(subDays(today, periodDays - 1)), 'yyyy-MM-dd');
      const chartEnd = format(endOfDay(today), 'yyyy-MM-dd');

      // Charger les statistiques générales
      const generalStatsData = await statisticsService.getGeneralStats();
      setGeneralStats(generalStatsData);

      // Charger les statistiques d'aujourd'hui
      const todayStatsData = await statisticsService.getGeneralStats({
        start_date: todayStart,
        end_date: todayEnd
      });
      setTodayStats(todayStatsData);

      // Charger les données temporelles pour les graphiques
      const timeSeriesDataResult = await statisticsService.getTimeSeriesData({
        period: 'day',
        start_date: chartStart,
        end_date: chartEnd
      });
      setTimeSeriesData(timeSeriesDataResult);

      // Mettre à jour les statistiques du tableau de bord
      updateDashboardStats(todayStatsData, generalStatsData);

    } catch (err: any) {
      setError('Erreur lors du chargement des données: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateDashboardStats = (todayData: StatisticsData, generalData: StatisticsData) => {
    const complianceRate = todayData.total_pesées > 0 
      ? ((todayData.pesées_complétées / todayData.total_pesées) * 100)
      : 0;

    setStats({
      total_weighings_today: todayData.total_pesées,
      total_rejections: todayData.pesées_refoulées,
      compliance_rate: Math.round(complianceRate * 10) / 10,
      total_stock: Math.round(generalData.poids_total_net / 1000),
      stock_capacity: 78
    });
  };

  // Données des graphiques basées sur les données réelles
  const weighingsChartData = {
    labels: timeSeriesData.map(item => format(new Date(item.date), 'dd/MM')),
    datasets: [
      {
        label: 'Pesées',
        data: timeSeriesData.map(item => item.total),
        borderColor: '#1B5E20',
        backgroundColor: 'rgba(27, 94, 32, 0.1)',
        tension: 0.4,
      },
    ],
  };

  const qualityChartData = {
    labels: ['Complétées', 'Refoulées', 'En Cours'],
    datasets: [
      {
        data: [
          todayStats?.pesées_complétées || 0,
          todayStats?.pesées_refoulées || 0,
          todayStats?.pesées_en_cours || 0
        ],
        backgroundColor: ['#10B981', '#EF4444', '#F59E0B'],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
    },
  };

  // Affichage du loading
  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement du tableau de bord...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex items-center">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            <span>{error}</span>
          </div>
          <button
            onClick={loadDashboardData}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-poppins font-semibold text-dark-black">
          Tableau de Bord
        </h1>
        <button
          onClick={loadDashboardData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <i className="fas fa-refresh mr-2"></i>
          Actualiser
        </button>
      </div>


      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Pesées aujourd'hui */}
        <div className="bg-white rounded-xl p-6 shadow-sm card-hover">
          <div className="flex items-center mb-2">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <ChartBarIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pesées aujourd'hui</p>
              <p className="text-3xl font-bold text-dark-black">{stats.total_weighings_today}</p>
            </div>
          </div>
          <p className="text-sm text-green-600 font-medium">
            {todayStats && generalStats ? 
              `Taux de complétion: ${Math.round((todayStats.pesées_complétées / Math.max(todayStats.total_pesées, 1)) * 100)}%` :
              'Aucune donnée'
            }
          </p>
        </div>
        
        {/* Refoulements */}
        <div className="bg-white rounded-xl p-6 shadow-sm card-hover">
          <div className="flex items-center mb-2">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Refoulements</p>
              <p className="text-3xl font-bold text-dark-black">{stats.total_rejections}</p>
            </div>
          </div>
          <p className="text-sm text-red-600">
            {todayStats ? 
              `Taux de refoulement: ${Math.round((todayStats.pesées_refoulées / Math.max(todayStats.total_pesées, 1)) * 100)}%` :
              'Aucune donnée'
            }
          </p>
        </div>
        
        {/* Conformité */}
        <div className="bg-white rounded-xl p-6 shadow-sm card-hover">
          <div className="flex items-center mb-2">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Conformité</p>
              <p className="text-3xl font-bold text-dark-black">{stats.compliance_rate}%</p>
            </div>
          </div>
          <p className="text-sm text-green-600">
            {stats.compliance_rate >= 95 ? 'Objectif atteint!' : 'Objectif: 95%'}
          </p>
        </div>
        
        {/* Stock total */}
        <div className="bg-white rounded-xl p-6 shadow-sm card-hover">
          <div className="flex items-center mb-2">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
              <CubeIcon className="h-6 w-6 text-bright-orange" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Stock total</p>
              <p className="text-3xl font-bold text-dark-black">{stats.total_stock.toLocaleString()}t</p>
            </div>
          </div>
          <p className="text-sm text-bright-orange">
            Poids net total: {generalStats ? `${Math.round(generalStats.poids_total_net / 1000)}t` : 'Aucune donnée'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Charts */}
        <div className="lg:col-span-2 space-y-8">
          {/* Pesées par jour Chart */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-dark-black">Pesées par jour</h3>
              <select 
                value={chartPeriod}
                onChange={(e) => setChartPeriod(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1"
              >
                <option value="7">7 derniers jours</option>
                <option value="30">30 derniers jours</option>
                <option value="90">3 derniers mois</option>
              </select>
            </div>
            <div className="h-64">
              <Line data={weighingsChartData} options={chartOptions} />
            </div>
          </div>

          {/* Répartition Qualité */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-medium text-dark-black mb-6">Répartition Qualité</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="h-48">
                <Pie data={qualityChartData} options={pieOptions} />
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-sm">Conforme</span>
                  </div>
                  <span className="font-semibold">96.2%</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-yellow-500 rounded-full mr-3"></div>
                    <span className="text-sm">Limite</span>
                  </div>
                  <span className="font-semibold">2.1%</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-red-500 rounded-full mr-3"></div>
                    <span className="text-sm">Non conforme</span>
                  </div>
                  <span className="font-semibold">1.7%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Actions rapides */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-medium text-dark-black mb-4">Actions rapides</h3>
            <div className="space-y-3">
              <PermissionGuard permission={PERMISSIONS.WEIGHING_CREATE}>
                <Link
                  to="/nouvelle-pesee"
                  onClick={handleNewWeighingClick}
                  className="w-full px-4 py-3 btn-primary text-white rounded-lg font-medium text-left flex items-center"
                >
                  <PlusIcon className="h-5 w-5 mr-3" />
                  Nouvelle pesée
                </Link>
              </PermissionGuard>
              
              <PermissionGuard permission={PERMISSIONS.STOCK_VIEW}>
                <Link
                  to="/stocks"
                  className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 text-dark-black rounded-lg font-medium text-left transition-colors flex items-center"
                >
                  <CubeIcon className="h-5 w-5 mr-3" />
                  Consulter stocks
                </Link>
              </PermissionGuard>
              
              <PermissionGuard permission={PERMISSIONS.QUALITY_ANALYSIS_VIEW}>
                <Link
                  to="/quality-analysis"
                  className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 text-dark-black rounded-lg font-medium text-left transition-colors flex items-center"
                >
                  <BeakerIcon className="h-5 w-5 mr-3" />
                  Analyse qualité
                </Link>
              </PermissionGuard>
            </div>
          </div>

          {/* Alertes récentes */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-medium text-dark-black mb-4">Statut des pesées</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Pesées refoulées</p>
                  <p className="text-xs text-gray-600">
                    {todayStats ? `${todayStats.pesées_refoulées} aujourd'hui` : 'Aucune donnée'}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Pesées en cours</p>
                  <p className="text-xs text-gray-600">
                    {todayStats ? `${todayStats.pesées_en_cours} en attente` : 'Aucune donnée'}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Pesées complétées</p>
                  <p className="text-xs text-gray-600">
                    {todayStats ? `${todayStats.pesées_complétées} aujourd'hui` : 'Aucune donnée'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Résumé des données */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-medium text-dark-black mb-4">Résumé des données</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <ChartBarIcon className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">Total des pesées</p>
                  <p className="text-xs text-gray-600">
                    {generalStats ? `${generalStats.total_pesées} pesées` : 'Aucune donnée'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CubeIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">Poids total brut</p>
                  <p className="text-xs text-gray-600">
                    {generalStats ? `${Math.round(generalStats.poids_total_brut / 1000)}t` : 'Aucune donnée'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <BeakerIcon className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium">Poids moyen</p>
                  <p className="text-xs text-gray-600">
                    {generalStats ? `${Math.round(generalStats.poids_moyen_brut / 1000)}t par pesée` : 'Aucune donnée'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
